import {
  buildConversationApiUrl,
  environment,
  parseTranscribeJobName,
} from "../../config/environment";
import React, { useEffect, useState } from "react";
import { InsightsTimeline } from "../Insights";
import labelMappings from "../Trace/labels.json";
import { customFetch } from "../../utils/Auth";
import { FaPencilAlt } from "react-icons/fa";
import ClientEventsTables from "./ClientEventsTables";
import { Toaster } from "../ui/Toaster";

const JobDetailsModal = ({ job, isOpen, onClose }) => {
  if (!isOpen || !job) return null;

  const isSummarizeJob = job.summarize_job_name;
  const isTranscribeJob = job.transcribe_job_name;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-ktx-purple">Job Details</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl font-bold"
            type="button"
          >
            ×
          </button>
        </div>
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">
                Job Information
              </h4>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium">Job Name:</span>{" "}
                  <span className="break-words">
                    {job.transcribe_job_name || job.summarize_job_name || "-"}
                  </span>
                </div>
                <div>
                  <span className="font-medium">Job Type:</span>{" "}
                  {isTranscribeJob
                    ? "Transcribe"
                    : isSummarizeJob
                    ? "Summarize"
                    : "-"}
                </div>
                <div>
                  <span className="font-medium">Status:</span>{" "}
                  {job.job_status || "-"}
                </div>
                <div>
                  <span className="font-medium">Platform:</span>{" "}
                  {job.transcribe_platform || job.summarize_platform || "-"}
                </div>
                <div>
                  <span className="font-medium">Model:</span>{" "}
                  {job.trainscribe_model || job.summarize_model || "-"}
                </div>
                <div>
                  <span className="font-medium">Is Scribed:</span>{" "}
                  {job.is_scribed ? "Yes" : "No"}
                </div>
                {isSummarizeJob && (
                  <>
                    <div>
                      <span className="font-medium">Is Reviewed:</span>{" "}
                      {job.is_reviewed ? "Yes" : "No"}
                    </div>
                    <div>
                      <span className="font-medium">Transcript Type:</span>{" "}
                      {job.transcript_type || "-"}
                    </div>
                  </>
                )}
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">
                Timing & Audio
              </h4>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium">Start Time:</span>{" "}
                  {job.job_start_time || "-"}
                </div>
                <div>
                  <span className="font-medium">End Time:</span>{" "}
                  {job.job_end_time || "-"}
                </div>
                <div>
                  <span className="font-medium">Audio Length:</span>{" "}
                  {job.audio_length ? `${job.audio_length}s` : "-"}
                </div>
                <div>
                  <span className="font-medium">Upload Date:</span>{" "}
                  {job.upload_date || job.audio_upload_date || "-"}
                </div>
                <div>
                  <span className="font-medium">Doctor:</span>{" "}
                  {job.Doctor || "-"}
                </div>
                <div>
                  <span className="font-medium">Patient ID:</span>{" "}
                  {job.patient_id || "-"}
                </div>
                {isSummarizeJob && (
                  <div>
                    <span className="font-medium">Reviewer ID:</span>{" "}
                    {job.reviewer_id || "-"}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mb-4">
            <h4 className="font-semibold text-gray-800 mb-2">URLs</h4>
            <div className="space-y-2 text-sm">
              {job.audio_url && (
                <div>
                  <span className="font-medium">Audio URL:</span>
                  <a
                    href={job.audio_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 break-all ml-2"
                  >
                    {job.audio_url}
                  </a>
                </div>
              )}
              {job.original_transcript_url && (
                <div>
                  <span className="font-medium">Transcript URL:</span>
                  <a
                    href={job.original_transcript_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 break-all ml-2"
                  >
                    {job.original_transcript_url}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Summarize Job Specific Sections */}
          {isSummarizeJob && (
            <>
              {/* SOAP Note Tags */}
              {job.soap_note_tags && (
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-800 mb-2">
                    SOAP Note Tags
                  </h4>
                  <div className="bg-gray-50 border border-gray-200 rounded p-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {Object.entries(job.soap_note_tags).map(
                        ([key, value]) => (
                          <div key={key}>
                            <span className="font-medium">{key}:</span>
                            <span className="ml-2">
                              {Array.isArray(value) ? value.join(", ") : value}
                            </span>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* MDM Suggested */}
              {job.mdm_suggested && (
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-800 mb-2">
                    MDM Suggested
                  </h4>
                  <div className="bg-gray-50 border border-gray-200 rounded p-3 max-h-48 overflow-y-auto">
                    <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono">
                      {typeof job.mdm_suggested === "string"
                        ? job.mdm_suggested
                        : JSON.stringify(job.mdm_suggested, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* ICD10 Codes */}
              {job.icd10_codes_v2 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-800 mb-2">
                    ICD10 Codes
                  </h4>
                  <div className="bg-gray-50 border border-gray-200 rounded p-3 max-h-48 overflow-y-auto">
                    <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono">
                      {typeof job.icd10_codes_v2 === "string"
                        ? job.icd10_codes_v2
                        : JSON.stringify(job.icd10_codes_v2, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Medical Content */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {job.history_of_present_illness && (
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">
                      History of Present Illness
                    </h4>
                    <div className="bg-gray-50 border border-gray-200 rounded p-3 max-h-48 overflow-y-auto">
                      <pre className="whitespace-pre-wrap text-sm text-gray-800">
                        {job.history_of_present_illness}
                      </pre>
                    </div>
                  </div>
                )}
                {job.assessment && (
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">
                      Assessment
                    </h4>
                    <div className="bg-gray-50 border border-gray-200 rounded p-3 max-h-48 overflow-y-auto">
                      <pre className="whitespace-pre-wrap text-sm text-gray-800">
                        {job.assessment}
                      </pre>
                    </div>
                  </div>
                )}
                {job.plan && (
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Plan</h4>
                    <div className="bg-gray-50 border border-gray-200 rounded p-3 max-h-48 overflow-y-auto">
                      <pre className="whitespace-pre-wrap text-sm text-gray-800">
                        {job.plan}
                      </pre>
                    </div>
                  </div>
                )}
                {job.interval_history && (
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">
                      Interval History
                    </h4>
                    <div className="bg-gray-50 border border-gray-200 rounded p-3 max-h-48 overflow-y-auto">
                      <pre className="whitespace-pre-wrap text-sm text-gray-800">
                        {job.interval_history}
                      </pre>
                    </div>
                  </div>
                )}
              </div>

              {/* SOAP Note */}
              {job.soap_note && (
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-800 mb-2">
                    SOAP Note
                  </h4>
                  <div className="bg-gray-50 border border-gray-200 rounded p-3 max-h-96 overflow-y-auto">
                    <pre className="whitespace-pre-wrap text-sm text-gray-800">
                      {job.soap_note}
                    </pre>
                  </div>
                </div>
              )}

              {/* Reference Jobs */}
              {job.reference_summarize_jobs &&
                job.reference_summarize_jobs.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-800 mb-2">
                      Reference Summarize Jobs
                    </h4>
                    <div className="bg-gray-50 border border-gray-200 rounded p-3">
                      <div className="space-y-1 text-sm">
                        {job.reference_summarize_jobs.map((refJob, index) => (
                          <div key={index} className="break-words">
                            {refJob}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

              {/* Ordered Medical Tests */}
              {job.ordered_medical_tests &&
                job.ordered_medical_tests.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-800 mb-2">
                      Ordered Medical Tests
                    </h4>
                    <div className="bg-gray-50 border border-gray-200 rounded p-3">
                      <div className="space-y-2 text-sm">
                        {job.ordered_medical_tests.map((test, index) => (
                          <div
                            key={index}
                            className="border-b border-gray-200 pb-2 last:border-b-0"
                          >
                            <div>
                              <span className="font-medium">Test Type:</span>{" "}
                              {test.test_type || "-"}
                            </div>
                            <div>
                              <span className="font-medium">Due Duration:</span>{" "}
                              {test.test_due_duration || "-"}
                            </div>
                            <div>
                              <span className="font-medium">Test Date:</span>{" "}
                              {test.test_date || "-"}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

              {/* CPT Codes */}
              {job.cpt_codes && job.cpt_codes.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-800 mb-2">
                    CPT Codes
                  </h4>
                  <div className="bg-gray-50 border border-gray-200 rounded p-3">
                    <div className="space-y-2 text-sm">
                      {job.cpt_codes.map((code, index) => (
                        <div
                          key={index}
                          className="border-b border-gray-200 pb-2 last:border-b-0"
                        >
                          {code &&
                          typeof code === "object" &&
                          (code["CPT Code"] ||
                            code["Description"] ||
                            code["Relevance"]) ? (
                            <>
                              <div>
                                <span className="font-medium">CPT Code:</span>{" "}
                                {code["CPT Code"] || "-"}
                              </div>
                              <div>
                                <span className="font-medium">
                                  Description:
                                </span>{" "}
                                {code["Description"] || "-"}
                              </div>
                              <div>
                                <span className="font-medium">Relevance:</span>{" "}
                                {code["Relevance"] || "-"}
                              </div>
                            </>
                          ) : (
                            <div>{String(code)}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Summarize Job Log */}
              {job.summarize_job_log && (
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-800 mb-2">
                    Summarize Job Log
                  </h4>
                  <div className="bg-gray-50 border border-gray-200 rounded p-3 max-h-96 overflow-y-auto">
                    <pre className="whitespace-pre-wrap text-xs text-gray-800 font-mono">
                      {JSON.stringify(job.summarize_job_log, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Original Transcript (for both types) */}
          {job.original_transcript && (
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">
                Original Transcript
              </h4>
              <div className="bg-gray-50 border border-gray-200 rounded p-3 max-h-96 overflow-y-auto">
                <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono">
                  {job.original_transcript}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Modal for showing full patient details
const PatientDetailsModal = ({ patient, isOpen, onClose, onPatientUpdated }) => {
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [localPatient, setLocalPatient] = useState(patient);
  const [successMsg, setSuccessMsg] = useState("");
  const [isFetchingAppointmentId, setIsFetchingAppointmentId] = useState(false);
  const [appointmentIdError, setAppointmentIdError] = useState("");

  useEffect(() => {
    setLocalPatient(patient);
    setIsFetchingAppointmentId(false);
    setAppointmentIdError("");
  }, [patient]);

  const isAppointmentIdEmpty = (value) =>
    value === null ||
    typeof value === "undefined" ||
    (typeof value === "string" && value.trim() === "");

  const handleGetAppointmentId = async () => {
    if (!localPatient?.patient_id || !localPatient?.mrn) {
      setAppointmentIdError(
        "Unable to retrieve appointment_id for this patient."
      );
      return;
    }

    setIsFetchingAppointmentId(true);
    setAppointmentIdError("");

    try {
      const response = await customFetch(
        `${environment.apiUrl}/patients/retrieve-appointment-id`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            patient_id: localPatient.patient_id,
          }),
        }
      );

      const result = await response.json().catch(() => ({}));
      const appointmentId = result?.appointment_id;

      if (!response.ok || !result?.success || !appointmentId) {
        throw new Error(result?.error || "Failed to retrieve appointment_id");
      }

      setLocalPatient((prev) => ({
        ...prev,
        appointment_id: appointmentId,
      }));

      if (typeof onPatientUpdated === "function") {
        onPatientUpdated((prev) => {
          if (!prev || prev.patient_id !== localPatient.patient_id) return prev;
          return {
            ...prev,
            appointment_id: appointmentId,
          };
        });
      }

      setSuccessMsg("Appointment ID updated successfully!");
      setTimeout(() => setSuccessMsg(""), 2000);
    } catch (err) {
      setAppointmentIdError(
        "Unable to retrieve appointment_id for this patient."
      );
    } finally {
      setIsFetchingAppointmentId(false);
    }
  };

  // The full details fields logic is reused from below
  const fullDetailsFields = [
    {
      key: "description",
      label: "Description",
      value: patient.description || "-",
    },
    { key: "patient_id", label: "Patient ID", value: patient.patient_id },
    { key: "mrn", label: "MRN", value: patient.mrn || "-" },
    {
      key: "date_of_appt",
      label: "Date of Appointment",
      value: patient.date_of_appt || "-",
    },
    {
      key: "patient_type",
      label: "Patient Type",
      value: patient.patient_type || "-",
    },
    { key: "user_id", label: "User ID", value: patient.user_id },
    {
      key: "created_at",
      label: "Created At",
      value: patient.created_at
        ? new Date(patient.created_at).toLocaleString()
        : "-",
    },
    {
      key: "appointment_id",
      label: "Appointment ID",
      value: patient.appointment_id,
    },
    {
      key: "date_of_birth",
      label: "Date of Birth",
      value: patient.date_of_birth,
    },
    { key: "status", label: "Status", value: patient.status || "-" },
    {
      key: "ehr_upload_status",
      label: "EHR Upload Status",
      value: patient.ehr_upload_status,
    },
    {
      key: "note_upload_status",
      label: "Note Upload Status",
      value: patient.note_upload_status,
    },
    { key: "import_type", label: "Import Type", value: patient.import_type },
    { key: "note", label: "Note", value: patient.note || "-" },
    {
      key: "coding_information",
      label: "Coding Information",
      value: patient.coding_information || "-",
    },
    { key: "los_code", label: "LOS Code", value: patient.los_code || "-" },
    {
      key: "time_spent",
      label: "Time Spent",
      value: patient.time_spent || "-",
    },
    {
      key: "time_based_code",
      label: "Time Based Code",
      value: patient.time_based_code || "-",
    },
    {
      key: "icd_10_codes",
      label: "ICD-10 Codes",
      value: patient.icd_10_codes || "-",
    },
    { key: "orders", label: "Orders", value: patient.orders || "-" },
    {
      key: "patient_data",
      label: "Patient Data",
      value: patient.patient_data || "-",
    },
    {
      key: "diarized_transcript",
      label: "Diarized Transcript",
      value: patient.diarized_transcript || "-",
    },
    {
      key: "diarized_transcript_csv_url",
      label: "Diarized Transcript CSV URL",
      value: patient.diarized_transcript_csv_url || "-",
    },
    {
      key: "chart_history",
      label: "Chart History",
      value: patient.chart_history ? (
        <pre className="whitespace-pre-wrap text-xs">
          {JSON.stringify(patient.chart_history, null, 2)}
        </pre>
      ) : (
        "-"
      ),
    },
    { key: "surgery", label: "Surgery", value: patient.surgery || "-" },
    { key: "hcc_codes", label: "HCC Codes", value: patient.hcc_codes || "-" },
    { key: "cpt_codes", label: "CPT Codes", value: patient.cpt_codes || "-" },
    { key: "hpi", label: "HPI", value: patient.hpi || "-" },
    {
      key: "interval_history",
      label: "Interval History",
      value: patient.interval_history || "-",
    },
    {
      key: "assessment",
      label: "Assessment",
      value: patient.assessment || "-",
    },
    { key: "plan", label: "Plan", value: patient.plan || "-" },
    { key: "e_and_m", label: "E&M Code", value: patient.e_and_m || "-" },
    { key: "orders_v2", label: "Orders - v2", value: patient.orders_v2 || "-" },
    {
      key: "icd10_codes_v2",
      label: "ICD10 Codes v2",
      value: patient.icd10_codes_v2 || "-",
    },
    { key: "facesheet", label: "Facesheet", value: patient.facesheet || "-" },
    {
      key: "mdm_suggested",
      label: "MDM Suggested",
      value: patient.mdm_suggested || "-",
    },
    {
      key: "mdm_selected",
      label: "MDM Selected",
      value: patient.mdm_selected || "-",
    },
    {
      key: "appt_time",
      label: "Appointment Time",
      value: patient.appt_time || "-",
    },
    {
      key: "cited_note",
      label: "Cited Note",
      value: patient.cited_note || "-",
    },
    { key: "macros", label: "Macros", value: patient.macros || "-" },
    {
      key: "patient_summary",
      label: "Patient Summary",
      value: patient.patient_summary || "-",
    },
    {
      key: "user_feedback",
      label: "User Feedback",
      value: patient.user_feedback || "-",
    },
  ].filter((field) => field && field.label);

  // Fields that should not be editable
  const nonEditableFields = ["patient_id", "user_id", "created_at"];

  // Fields that should use a textarea for editing
  const multilineFields = [
    "note",
    "coding_information",
    "patient_data",
    "diarized_transcript",
    "hpi",
    "interval_history",
    "assessment",
    "plan",
    "e_and_m",
    "orders_v2",
  ];

  // Status options for dropdown
  const statusOptions = [
    "Ready to Record",
    "Uploading Audio",
    "Processing Audio",
    "Review",
    "Locked",
  ];

  // Patient type options for dropdown
  const patientTypeOptions = ["new", "fu", "telemed"];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-ktx-purple">
            Full Patient Details
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl font-bold"
            type="button"
          >
            ×
          </button>
        </div>
        {successMsg && (
          <div className="bg-green-100 text-green-800 px-4 py-2 text-sm text-center">
            {successMsg}
          </div>
        )}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Vertical label-content layout */}
          <div className="space-y-5">
            {fullDetailsFields.map((field) => (
              <div key={field.key}>
                <div className="flex items-center mb-1">
                  <div className="font-semibold text-gray-800 mr-2">
                    {field.label}
                  </div>
                  {/* Pencil icon for editing, only if editable */}
                  {editingField !== field.key &&
                    !nonEditableFields.includes(field.key) && (
                      <button
                        className="ml-1 text-gray-400 hover:text-ktx-purple focus:outline-none"
                        onClick={() => {
                          setEditingField(field.key);
                          setEditValue(
                            typeof localPatient[field.key] === "string"
                              ? localPatient[field.key]
                              : typeof localPatient[field.key] === "object" &&
                                localPatient[field.key] !== null &&
                                !Array.isArray(localPatient[field.key]) &&
                                !React.isValidElement(localPatient[field.key])
                              ? JSON.stringify(localPatient[field.key], null, 2)
                              : ""
                          );
                        }}
                        type="button"
                        aria-label={`Edit ${field.label}`}
                      >
                        <FaPencilAlt size={14} />
                      </button>
                    )}
                </div>
                <div className="break-words text-gray-700">
                  {editingField === field.key ? (
                    <div className="flex items-center gap-2">
                      {field.key === "status" ? (
                        <select
                          className="border rounded px-2 py-1 w-full"
                          value={editValue}
                          onChange={async (e) => {
                            const newValue = e.target.value;
                            setEditValue(newValue);
                            try {
                              const response = await customFetch(
                                `${environment.apiUrl}/patients/update-field`,
                                {
                                  method: "PATCH",
                                  headers: {
                                    "Content-Type": "application/json",
                                  },
                                  body: JSON.stringify({
                                    patient_id: localPatient.patient_id,
                                    field: field.key,
                                    value: newValue,
                                  }),
                                }
                              );
                              const result = await response.json();
                              if (response.ok && result.success) {
                                setLocalPatient((prev) => ({
                                  ...prev,
                                  [field.key]: newValue,
                                }));
                                setSuccessMsg(
                                  `${field.label} updated successfully!`
                                );
                                setTimeout(() => setSuccessMsg(""), 2000);
                              } else {
                                alert(
                                  result.error || "Failed to update field."
                                );
                                setEditValue(localPatient[field.key] || ""); // Revert on error
                              }
                            } catch (err) {
                              alert("Error updating field.");
                              setEditValue(localPatient[field.key] || ""); // Revert on error
                            }
                          }}
                          autoFocus
                        >
                          {statusOptions.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : field.key === "patient_type" ? (
                        <select
                          className="border rounded px-2 py-1 w-full"
                          value={editValue}
                          onChange={async (e) => {
                            const newValue = e.target.value;
                            setEditValue(newValue);
                            try {
                              const response = await customFetch(
                                `${environment.apiUrl}/patients/update-field`,
                                {
                                  method: "PATCH",
                                  headers: {
                                    "Content-Type": "application/json",
                                  },
                                  body: JSON.stringify({
                                    patient_id: localPatient.patient_id,
                                    field: field.key,
                                    value: newValue,
                                  }),
                                }
                              );
                              const result = await response.json();
                              if (response.ok && result.success) {
                                setLocalPatient((prev) => ({
                                  ...prev,
                                  [field.key]: newValue,
                                }));
                                setSuccessMsg(
                                  `${field.label} updated successfully!`
                                );
                                setTimeout(() => setSuccessMsg(""), 2000);
                              } else {
                                alert(
                                  result.error || "Failed to update field."
                                );
                                setEditValue(localPatient[field.key] || ""); // Revert on error
                              }
                            } catch (err) {
                              alert("Error updating field.");
                              setEditValue(localPatient[field.key] || ""); // Revert on error
                            }
                          }}
                          autoFocus
                        >
                          <option value="">Select patient type...</option>
                          {patientTypeOptions.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : multilineFields.includes(field.key) ? (
                        <textarea
                          className="border rounded px-2 py-1 w-full min-h-[80px]"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          autoFocus
                        />
                      ) : (
                        <input
                          className="border rounded px-2 py-1 w-full"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          autoFocus
                        />
                      )}
                      {field.key !== "status" &&
                        field.key !== "patient_type" && (
                          <>
                            <button
                              className="text-xs text-green-600 underline"
                              onClick={async () => {
                                try {
                                  const response = await customFetch(
                                    `${environment.apiUrl}/patients/update-field`,
                                    {
                                      method: "PATCH",
                                      headers: {
                                        "Content-Type": "application/json",
                                      },
                                      body: JSON.stringify({
                                        patient_id: localPatient.patient_id,
                                        field: field.key,
                                        value: editValue,
                                      }),
                                    }
                                  );
                                  const result = await response.json();
                                  if (response.ok && result.success) {
                                    setLocalPatient((prev) => ({
                                      ...prev,
                                      [field.key]: editValue,
                                    }));
                                    setEditingField(null);
                                    setSuccessMsg(
                                      `${field.label} updated successfully!`
                                    );
                                    setTimeout(() => setSuccessMsg(""), 2000);
                                  } else {
                                    alert(
                                      result.error || "Failed to update field."
                                    );
                                  }
                                } catch (err) {
                                  alert("Error updating field.");
                                }
                              }}
                              type="button"
                            >
                              Save
                            </button>

                            <button
                              className="text-xs text-gray-600 underline"
                              onClick={() => setEditingField(null)}
                              type="button"
                            >
                              Cancel
                            </button>
                          </>
                        )}
                    </div>
                  ) : field.key === "appointment_id" ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <span>
                        {isAppointmentIdEmpty(localPatient[field.key])
                          ? "-"
                          : localPatient[field.key]}
                      </span>
                      {isAppointmentIdEmpty(localPatient[field.key]) && (
                        <>
                          <button
                            className="px-2 py-1 text-xs bg-ktx-purple text-white rounded hover:bg-purple-700 disabled:opacity-60 disabled:cursor-not-allowed"
                            onClick={handleGetAppointmentId}
                            disabled={isFetchingAppointmentId}
                            type="button"
                          >
                            {isFetchingAppointmentId ? (
                              <span className="inline-flex items-center gap-2">
                                <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Getting...
                              </span>
                            ) : (
                              "Get Appointment ID"
                            )}
                          </button>
                          {appointmentIdError && (
                            <span className="text-xs text-red-600">
                              {appointmentIdError}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  ) : typeof localPatient[field.key] === "object" &&
                    !Array.isArray(localPatient[field.key]) &&
                    localPatient[field.key] !== null &&
                    !React.isValidElement(localPatient[field.key]) ? (
                    <pre className="whitespace-pre-wrap text-xs">
                      {JSON.stringify(localPatient[field.key], null, 2)}
                    </pre>
                  ) : (
                    localPatient[field.key] ?? "-"
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const PatientInfo = ({
  selectedPatient,
  setSelectedPatient,
  showFullPatientDetails,
  setShowFullPatientDetails,
  isEditPatientMode,
  setIsEditPatientMode,
  showPatientUpdateMsg,
  setShowPatientUpdateMsg,
  showTrace,
  setShowTrace,
  traceData,
  fetchTrace,
  fetchJobsByPatient,
}) => {
  const [activeSection, setActiveSection] = useState(null);
  const [jobsByPatientData, setJobsByPatientData] = useState(null);
  const [summarizeJobsData, setSummarizeJobsData] = useState(null);
  const [isLoadingJobsByPatient, setIsLoadingJobsByPatient] = useState(false);
  const [isLoadingSummarizeJobs, setIsLoadingSummarizeJobs] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isJobModalOpen, setIsJobModalOpen] = useState(false);
  const [isPatientDetailsModalOpen, setIsPatientDetailsModalOpen] =
    useState(false);
  const [copiedText, setCopiedText] = useState(null);
  const [clientEvents, setClientEvents] = useState([]);
  const [isLoadingClientEvents, setIsLoadingClientEvents] = useState(false);
  const [clientEventsError, setClientEventsError] = useState(null);

  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(`${fieldName} copied to clipboard!`);
      setTimeout(() => setCopiedText(null), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
      setCopiedText("Failed to copy to clipboard");
      setTimeout(() => setCopiedText(null), 2000);
    }
  };

  const handleMergeTranscript = async (conversationId) => {
    try {
      const conversationResponse = await fetch(
        buildConversationApiUrl(),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            conversation_id: conversationId,
          }),
        }
      );

      if (conversationResponse.ok) {
        const conversationData = await conversationResponse.json();
        console.log("Conversation response:", conversationData);
        setCopiedText("Merge transcript initiated successfully!");
        setTimeout(() => setCopiedText(null), 2000);
      } else {
        console.error(
          "Failed to initiate merge transcript:",
          conversationResponse.status
        );
        setCopiedText("Failed to initiate merge transcript. Please try again.");
        setTimeout(() => setCopiedText(null), 2000);
      }
    } catch (conversationErr) {
      console.error("Error making conversation request:", conversationErr);
      setCopiedText("Error initiating merge transcript. Please try again.");
      setTimeout(() => setCopiedText(null), 2000);
    }
  };

  // Function to fetch jobs data when Transcribe Job button is clicked
  const handleTranscribeJobClick = async () => {
    if (!selectedPatient || !selectedPatient.patient_id) return;

    if (activeSection === "transcription") {
      // Hide transcription section
      setActiveSection(null);
      setJobsByPatientData(null);
    } else {
      // Show transcription section and hide others
      setActiveSection("transcription");
      setSummarizeJobsData(null);
      setShowTrace(false);
      setClientEvents([]);
      setClientEventsError(null);

      if (!jobsByPatientData) {
        // First time clicking - fetch data
        setIsLoadingJobsByPatient(true);
        try {
          const response = await customFetch(
            `${environment.apiUrl}/patients/transcribe-jobs/by-patient/${selectedPatient.patient_id}`,
            {
              method: "GET",
            }
          );
          if (!response.ok) throw new Error("Failed to fetch jobs by patient");
          const data = await response.json();
          setJobsByPatientData(data);
          console.log("Jobs by patient data:", data);
        } catch (err) {
          console.error("Failed to fetch jobs by patient:", err);
          setJobsByPatientData({
            error: "Failed to fetch jobs by patient data",
          });
        } finally {
          setIsLoadingJobsByPatient(false);
        }
      }
    }
  };

  // Function to fetch summarize jobs data when Summarization button is clicked
  const handleSummarizationClick = async () => {
    if (!selectedPatient || !selectedPatient.patient_id) return;

    if (activeSection === "summarization") {
      // Hide summarization section
      setActiveSection(null);
      setSummarizeJobsData(null);
    } else {
      // Show summarization section and hide others
      setActiveSection("summarization");
      setJobsByPatientData(null);
      setShowTrace(false);
      setClientEvents([]);
      setClientEventsError(null);

      if (!summarizeJobsData) {
        // First time clicking - fetch data
        setIsLoadingSummarizeJobs(true);
        try {
          const response = await customFetch(
            `${environment.apiUrl}/patients/summarize-jobs/by-patient/${selectedPatient.patient_id}`,
            {
              method: "GET",
            }
          );
          if (!response.ok)
            throw new Error("Failed to fetch summarize jobs by patient");
          const data = await response.json();
          setSummarizeJobsData(data);
          console.log("Summarize jobs by patient data:", data);
        } catch (err) {
          console.error("Failed to fetch summarize jobs by patient:", err);
          setSummarizeJobsData({
            error: "Failed to fetch summarize jobs by patient data",
          });
        } finally {
          setIsLoadingSummarizeJobs(false);
        }
      }
    }
  };

  // Function to handle trace button click
  const handleTraceClick = async () => {
    if (!selectedPatient || !selectedPatient.patient_id) return;

    if (activeSection === "trace") {
      // Hide trace section
      setActiveSection(null);
      setShowTrace(false);
    } else {
      // Show trace section and hide others
      setActiveSection("trace");
      setJobsByPatientData(null);
      setSummarizeJobsData(null);
      setClientEvents([]);
      setClientEventsError(null);

      if (!showTrace) {
        setShowTrace(true);
        await fetchTrace(selectedPatient.patient_id);
      }
    }
  };

  // Function to handle client events button click
  const handleClientEventsClick = async () => {
    if (!selectedPatient || !selectedPatient.patient_id) return;

    if (activeSection === "client-events") {
      // Hide client events section
      setActiveSection(null);
      setClientEvents([]);
      setClientEventsError(null);
    } else {
      // Show client events section and hide others
      setActiveSection("client-events");
      setJobsByPatientData(null);
      setSummarizeJobsData(null);
      setShowTrace(false);

      // Fetch client events for this patient
      setIsLoadingClientEvents(true);
      setClientEventsError(null);
      try {
        const patientId = selectedPatient.patient_id.toString();
        const params = new URLSearchParams();
        params.append("patientId", patientId);

        const response = await customFetch(
          `${
            environment.apiUrl
          }/doctors/client-logs/search?${params.toString()}`,
          { method: "GET" }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch client events");
        }

        const data = await response.json();
        setClientEvents(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching client events:", err);
        setClientEventsError("Failed to fetch client events: " + err.message);
        setClientEvents([]);
      } finally {
        setIsLoadingClientEvents(false);
      }
    }
  };

  if (!selectedPatient) return null;

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4 w-full overflow-auto">
      <h3 className="text-lg font-semibold text-ktx-purple mb-2">
        Patient Details
      </h3>
      <div className="overflow-x-auto mb-2">
        <table className="min-w-[350px] w-full text-sm table-fixed">
          <tbody>
            {/* Always show summary fields */}
            <tr>
              <td className="font-semibold pr-2 py-1 min-w-[140px]">Name</td>
              <td className="break-words">
                {selectedPatient.description || "-"}
              </td>
            </tr>
            <tr>
              <td className="font-semibold pr-2 py-1 min-w-[140px]">
                Patient ID
              </td>
              <td className="break-words">
                {selectedPatient.patient_id || "-"}
              </td>
            </tr>
            <tr>
              <td className="font-semibold pr-2 py-1 min-w-[140px]">
                Appointment ID
              </td>
              <td className="break-words">
                {selectedPatient.appointment_id || "-"}
              </td>
            </tr>
            <tr>
              <td className="font-semibold pr-2 py-1 min-w-[140px]">MRN</td>
              <td className="break-words">{selectedPatient.mrn || "-"}</td>
            </tr>
            <tr>
              <td className="font-semibold pr-2 py-1 min-w-[140px]">Status</td>
              <td className="break-words">{selectedPatient.status || "-"}</td>
            </tr>
            <tr>
              <td className="font-semibold pr-2 py-1 min-w-[140px]">
                Appointment Date
              </td>
              <td className="break-words">
                {selectedPatient.date_of_appt || "-"}
              </td>
            </tr>
            <tr>
              <td className="font-semibold pr-2 py-1 min-w-[140px]">
                Appointment Time
              </td>
              <td className="break-words">
                {selectedPatient.appt_time || "-"}
              </td>
            </tr>
            <tr>
              <td className="font-semibold pr-2 py-1 min-w-[140px]">Doctor</td>
              <td className="break-words">
                {selectedPatient.doctor_name || "-"}
              </td>
            </tr>
            <tr>
              <td className="font-semibold pr-2 py-1 min-w-[140px]">
                Patient Type
              </td>
              <td className="break-words">
                {selectedPatient.patient_type || "-"}
              </td>
            </tr>
            <tr>
              <td className="font-semibold pr-2 py-1 min-w-[140px]">
                Onco EMR Link
              </td>
              <td className="break-words">
                {selectedPatient.onco_emr_url ? (
                  <a
                    href={selectedPatient.onco_emr_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-ktx-purple underline break-all"
                  >
                    {selectedPatient.onco_emr_url}
                  </a>
                ) : (
                  "-"
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {/* Details/Edit/Save/Cancel buttons row */}
      <div className="flex justify-end mt-2 gap-2">
        {/* Show full details button when details are hidden */}
        {!showFullPatientDetails && !isEditPatientMode && (
          <button
            className="px-4 py-2 bg-ktx-purple text-white rounded hover:bg-purple-700 transition-colors text-sm font-medium"
            onClick={() => setIsPatientDetailsModalOpen(true)}
            type="button"
            style={{ marginRight: "auto" }}
          >
            Patient Details
          </button>
        )}
      </div>
      {/* Action button row */}
      <div className="flex flex-row gap-3 mt-4">
        {/* Re-upload button removed */}
      </div>
      {/* Patient operation buttons row */}
      <div className="flex flex-row gap-3 mt-3">
        <button
          className={`px-4 py-2 text-white rounded transition-colors text-sm ${
            activeSection === "transcription"
              ? "bg-ktx-purple border-2 border-white shadow-lg"
              : "bg-gray-600 hover:bg-gray-700"
          }`}
          type="button"
          onClick={handleTranscribeJobClick}
        >
          {activeSection === "transcription"
            ? "Hide Transcription"
            : "Transcription"}
        </button>
        <button
          className={`px-4 py-2 text-white rounded transition-colors text-sm ${
            activeSection === "summarization"
              ? "bg-ktx-purple border-2 border-white shadow-lg"
              : "bg-gray-600 hover:bg-gray-700"
          }`}
          type="button"
          onClick={handleSummarizationClick}
        >
          {activeSection === "summarization"
            ? "Hide Summarization"
            : "Summarization"}
        </button>
        <button
          className={`px-4 py-2 text-white rounded transition-colors text-sm ${
            activeSection === "trace"
              ? "bg-ktx-purple border-2 border-white shadow-lg"
              : "bg-gray-600 hover:bg-gray-700"
          }`}
          type="button"
          onClick={handleTraceClick}
        >
          {activeSection === "trace" ? "Hide Insights" : "Insights"}
        </button>
        <button
          className={`px-4 py-2 text-white rounded transition-colors text-sm ${
            activeSection === "client-events"
              ? "bg-ktx-purple border-2 border-white shadow-lg"
              : "bg-gray-600 hover:bg-gray-700"
          }`}
          type="button"
          onClick={handleClientEventsClick}
        >
          {activeSection === "client-events"
            ? "Hide Client Events"
            : "Client Events"}
        </button>
      </div>
      {/* Jobs Data Display Section */}
      {activeSection === "transcription" && (
        <div className="mt-4">
          <h4 className="text-md font-semibold text-ktx-purple mb-2">
            Transcribe Jobs
          </h4>
          {isLoadingJobsByPatient ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-gray-600">Loading jobs data...</p>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              {jobsByPatientData.error ? (
                <div className="text-red-600 text-sm">
                  {jobsByPatientData.error}
                </div>
              ) : Array.isArray(jobsByPatientData) &&
                jobsByPatientData.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-300">
                        <th className="text-left py-2 px-2">Job Name</th>
                        <th className="text-left py-2 px-2">Conversation ID</th>
                        <th className="text-left py-2 px-2">Doctor</th>
                        <th className="text-left py-2 px-2">Service</th>
                        <th className="text-left py-2 px-2">Model</th>
                        <th className="text-left py-2 px-2">Audio Length</th>
                        <th className="text-left py-2 px-2">Start Time</th>
                        <th className="text-left py-2 px-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobsByPatientData.map((job, index) => {
                        const jobName =
                          job.transcribe_job_name ||
                          job.summarize_job_name ||
                          "";
                        let conversationId = "-";

                        if (jobName.includes(".mp3")) {
                          const extractedPart = parseTranscribeJobName(
                            jobName.slice(0, jobName.indexOf(".mp3") + 4),
                            { stripMp3: true }
                          );
                          if (extractedPart !== null) {
                          // Replace hyphens with forward slashes for date parts
                          // Pattern: Dr.Dharia-6-23-2025-09:13:26AM-499011 -> Dr.Dharia/6/23/2025-09:13:26AM-499011
                          conversationId = extractedPart.replace(
                            /-(\d+)-(\d+)-(\d+)/,
                            "/$1/$2/$3"
                          );
                          }
                        }

                        return (
                          <tr key={index} className="border-b border-gray-200">
                            <td className="py-2 px-2 break-words">
                              <div className="flex items-center gap-2">
                                <span>{jobName || "-"}</span>
                                {jobName && (
                                  <button
                                    onClick={() =>
                                      copyToClipboard(jobName, "Job name")
                                    }
                                    className="text-xs bg-gray-500 text-white p-1 rounded hover:bg-gray-600 transition-colors"
                                    type="button"
                                    title="Copy Job Name"
                                  >
                                    <svg
                                      className="w-3 h-3"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                      ></path>
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="py-2 px-2 break-words">
                              <div className="flex items-center gap-2">
                                <span>{conversationId}</span>
                                {conversationId !== "-" && (
                                  <button
                                    onClick={() =>
                                      copyToClipboard(
                                        conversationId,
                                        "Conversation ID"
                                      )
                                    }
                                    className="text-xs bg-gray-500 text-white p-1 rounded hover:bg-gray-600 transition-colors"
                                    type="button"
                                    title="Copy Conversation ID"
                                  >
                                    <svg
                                      className="w-3 h-3"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                      ></path>
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="py-2 px-2">{job.Doctor || "-"}</td>
                            <td className="py-2 px-2">
                              {job.transcribe_platform || "-"}
                            </td>
                            <td className="py-2 px-2">
                              {job.trainscribe_model || "-"}
                            </td>
                            <td className="py-2 px-2">
                              {job.audio_length ? `${job.audio_length}s` : "-"}
                            </td>
                            <td className="py-2 px-2">
                              {job.job_start_time
                                ? new Date(job.job_start_time).toLocaleString()
                                : "-"}
                            </td>
                            <td className="py-2 px-2">
                              <div className="flex gap-1">
                                <button
                                  onClick={() => {
                                    setSelectedJob(job);
                                    setIsJobModalOpen(true);
                                  }}
                                  className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 transition-colors"
                                  type="button"
                                >
                                  View Details
                                </button>
                                {conversationId !== "-" && (
                                  <button
                                    onClick={() =>
                                      handleMergeTranscript(conversationId)
                                    }
                                    className="text-xs bg-orange-600 text-white px-2 py-1 rounded hover:bg-orange-700 transition-colors"
                                    type="button"
                                    title="Merge Transcript"
                                  >
                                    Merge Transcript
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-gray-600 text-sm">
                  No jobs found for this patient.
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {activeSection === "summarization" && (
        <div className="mt-4">
          <h4 className="text-md font-semibold text-ktx-purple mb-2">
            Summarize Jobs
          </h4>
          {isLoadingSummarizeJobs ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-gray-600">Loading summarize jobs data...</p>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              {summarizeJobsData.error ? (
                <div className="text-red-600 text-sm">
                  {summarizeJobsData.error}
                </div>
              ) : Array.isArray(summarizeJobsData) &&
                summarizeJobsData.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-300">
                        <th className="text-left py-2 px-2">Job Name</th>
                        <th className="text-left py-2 px-2">Doctor</th>
                        <th className="text-left py-2 px-2">Service</th>
                        <th className="text-left py-2 px-2">Model</th>
                        <th className="text-left py-2 px-2">Start Time</th>
                        <th className="text-left py-2 px-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summarizeJobsData.map((job, index) => (
                        <tr key={index} className="border-b border-gray-200">
                          <td className="py-2 px-2 break-words">
                            <div className="flex items-center gap-2">
                              <span>{job.summarize_job_name || "-"}</span>
                              {job.summarize_job_name && (
                                <button
                                  onClick={() =>
                                    copyToClipboard(
                                      job.summarize_job_name,
                                      "Job name"
                                    )
                                  }
                                  className="text-xs bg-gray-500 text-white p-1 rounded hover:bg-gray-600 transition-colors"
                                  type="button"
                                  title="Copy Job Name"
                                >
                                  <svg
                                    className="w-3 h-3"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                    ></path>
                                  </svg>
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="py-2 px-2">{job.Doctor || "-"}</td>
                          <td className="py-2 px-2">
                            {job.summarize_platform || "-"}
                          </td>
                          <td className="py-2 px-2">
                            {job.summarize_model || "-"}
                          </td>
                          <td className="py-2 px-2">
                            {job.job_start_time
                              ? new Date(job.job_start_time).toLocaleString()
                              : "-"}
                          </td>
                          <td className="py-2 px-2">
                            <button
                              onClick={() => {
                                setSelectedJob(job);
                                setIsJobModalOpen(true);
                              }}
                              className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 transition-colors"
                              type="button"
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-gray-600 text-sm">
                  No summarize jobs found for this patient.
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {activeSection === "trace" && traceData && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 mt-4 w-full overflow-auto">
          <h4 className="text-md font-semibold text-ktx-purple mb-2">
            Insights
          </h4>
          {Array.isArray(traceData) && traceData.length > 0 ? (
            <InsightsTimeline data={traceData} labelMappings={labelMappings} />
          ) : traceData && traceData.error ? (
            <div className="text-red-600 text-sm">{traceData.error}</div>
          ) : (
            <div className="text-gray-600 text-sm">No insight events found.</div>
          )}
        </div>
      )}
      {activeSection === "client-events" && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 mt-4 w-full overflow-auto">
          <h4 className="text-md font-semibold text-ktx-purple mb-2">
            Client Events
          </h4>
          <ClientEventsTables
            logs={clientEvents}
            isLoading={isLoadingClientEvents}
            error={clientEventsError}
          />
        </div>
      )}
      {isJobModalOpen && selectedJob && (
        <JobDetailsModal
          job={selectedJob}
          isOpen={isJobModalOpen}
          onClose={() => {
            setIsJobModalOpen(false);
            setSelectedJob(null);
          }}
        />
      )}
      {/* Patient Details Modal */}
      {isPatientDetailsModalOpen && (
        <PatientDetailsModal
          patient={selectedPatient}
          isOpen={isPatientDetailsModalOpen}
          onClose={() => setIsPatientDetailsModalOpen(false)}
          onPatientUpdated={setSelectedPatient}
        />
      )}
      {/* Toast notification for copied text */}
      {copiedText && (
        <div className="fixed bottom-4 right-4 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded shadow-lg z-50">
          <div className="flex items-center">
            <svg
              className="w-5 h-5 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              ></path>
            </svg>
            <p>{copiedText}</p>
          </div>
        </div>
      )}
      <Toaster />
    </div>
  );
};

export default PatientInfo;
